"use client";

// ============================================================
// CLUBHUB – Leiste (Aufbau und Gestaltung wie im Agent)
// ============================================================
// Schwebende Karte unter der Kopfzeile, klebt beim Scrollen, laesst
// sich auf Symbolbreite einklappen. Oben das Produkt, darunter die
// Bereiche in Gruppen, unten Mein Bereich, Einstellungen und die Person.
// Auf dem Telefon gibt es sie nicht — dort traegt `mobile-nav.tsx`.
// ============================================================
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV_FUSS, NAV_GRUPPEN, NAV_KONTO, NAVIGATION, istAktiv, type NavEintrag } from "@/lib/navigation";
import { useLeiste } from "@/lib/use-leiste";
import { ClubhubMark } from "./clubhub-mark";
import type { UserView } from "@/lib/views";

function Eintrag({ e, aktiv, eingeklappt }: { e: NavEintrag; aktiv: boolean; eingeklappt: boolean }) {
  const Icon = e.icon;
  return (
    <Link
      href={e.href}
      aria-current={aktiv ? "page" : undefined}
      title={eingeklappt ? `${e.label} — ${e.hinweis}` : e.hinweis}
      className={cn(
        "group text-body-sm relative flex items-center rounded-lg",
        "duration-fast ease-standard transition-colors",
        eingeklappt ? "justify-center px-0 py-2" : "gap-2.5 px-2.5 py-[7px]",
        aktiv
          ? "bg-bg-tertiary text-text-primary font-medium"
          : "text-text-secondary hover:bg-bg-secondary hover:text-text-primary",
      )}
    >
      {!eingeklappt && (
        <span
          aria-hidden
          className={cn(
            "bg-accent absolute top-1/2 left-0 h-4 w-[3px] -translate-y-1/2 rounded-r-full",
            "duration-fast transition-opacity",
            aktiv ? "opacity-100" : "opacity-0",
          )}
        />
      )}
      <Icon
        aria-hidden
        className={cn(
          "duration-fast h-4 w-4 shrink-0 transition-colors",
          aktiv ? "text-accent" : "text-text-tertiary group-hover:text-text-secondary",
        )}
      />
      {!eingeklappt && e.label}
    </Link>
  );
}

export function Sidebar({ user }: { user: UserView }) {
  const pathname = usePathname();
  const { eingeklappt, umschalten } = useLeiste();

  return (
    <aside
      className={cn(
        "sticky top-[calc(var(--ans-topbar)+12px)] z-20 hidden lg:flex",
        "m-3 mr-0 h-[calc(100dvh-var(--ans-topbar)-24px)] shrink-0 flex-col overflow-hidden",
        "bg-bg-elevated shadow-e2 rounded-2xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]",
        "duration-normal ease-standard transition-[width]",
        eingeklappt ? "w-[68px]" : "w-sidebar",
      )}
    >
      {/* Kopf: das Produkt */}
      {eingeklappt ? (
        <div className="flex h-14 shrink-0 items-center justify-center">
          <button
            type="button"
            onClick={umschalten}
            aria-label="Navigation ausklappen"
            title="Ausklappen"
            className="text-text-tertiary duration-fast hover:bg-bg-secondary hover:text-text-primary grid h-7 w-7 place-items-center rounded-md transition-colors"
          >
            <ChevronsRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : (
        <div className="flex h-14 shrink-0 items-center gap-2.5 px-4">
          <ClubhubMark />
          <span className="text-heading-sm text-text-primary flex-1 truncate tracking-tight">clubhub</span>
          <button
            type="button"
            onClick={umschalten}
            aria-label="Navigation einklappen"
            title="Einklappen"
            className="text-text-tertiary duration-fast hover:bg-bg-secondary hover:text-text-primary grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors"
          >
            <ChevronsLeft className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      {/* Bereiche */}
      <nav
        aria-label="Bereiche"
        className={cn(
          "flex-1 overflow-x-hidden overflow-y-auto",
          eingeklappt ? "space-y-2 px-3 py-1" : "space-y-4 px-2.5 py-1",
        )}
      >
        <div className="space-y-0.5">
          {NAVIGATION.filter((e) => e.gruppe === null).map((e) => (
            <Eintrag key={e.href} e={e} aktiv={istAktiv(pathname, e.href)} eingeklappt={eingeklappt} />
          ))}
        </div>
        {NAV_GRUPPEN.map((gruppe) => (
          <div key={gruppe} className="space-y-0.5">
            {eingeklappt ? (
              <div aria-hidden className="bg-border-subtle mx-auto mb-2 h-px w-6" />
            ) : (
              <p className="text-text-tertiary px-2.5 pb-1 text-[10px] font-medium tracking-[0.08em] uppercase">
                {gruppe}
              </p>
            )}
            {NAVIGATION.filter((e) => e.gruppe === gruppe).map((e) => (
              <Eintrag key={e.href} e={e} aktiv={istAktiv(pathname, e.href)} eingeklappt={eingeklappt} />
            ))}
          </div>
        ))}
      </nav>

      {/* Fuss: Mein Bereich, Einstellungen, Person */}
      <div className={cn("border-border-subtle shrink-0 space-y-0.5 border-t py-2.5", eingeklappt ? "px-3" : "px-2.5")}>
        {NAV_FUSS.map((e) => (
          <Eintrag key={e.href} e={e} aktiv={istAktiv(pathname, e.href)} eingeklappt={eingeklappt} />
        ))}
        <Link
          href={NAV_KONTO.href}
          aria-current={istAktiv(pathname, NAV_KONTO.href) ? "page" : undefined}
          title={eingeklappt ? `${user.firstName} ${user.lastName} — Mein Konto` : "Mein Konto öffnen"}
          className={cn(
            "group mt-1.5 flex min-w-0 items-center rounded-lg py-1.5",
            "duration-fast ease-standard transition-colors",
            eingeklappt ? "justify-center px-0" : "gap-2.5 px-2.5",
            istAktiv(pathname, NAV_KONTO.href) ? "bg-bg-tertiary" : "hover:bg-bg-secondary",
          )}
        >
          <div className="bg-accent-2-subtle text-accent-2 grid h-7 w-7 shrink-0 place-items-center rounded-full text-[10px] font-medium">
            {user.firstName[0]}
            {user.lastName[0]}
          </div>
          {!eingeklappt && (
            <div className="min-w-0 flex-1">
              <p className="text-body-xs text-text-primary truncate font-medium">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-caption text-text-tertiary truncate">{user.email}</p>
            </div>
          )}
        </Link>
      </div>
    </aside>
  );
}
